import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChevronDown, ArrowRight } from "lucide-react";

const COLORS = [
  "#C2603F", "#E0A82E", "#C64B2C", "#7E8B3B", "#D9793A", "#5E7B3A",
  "#B0553A", "#CE8E1E", "#A8532B", "#6F7F35", "#D96A4A", "#B06A2C",
  "#8C6A2B", "#C2843A",
];

const GiraffeIcon = ({ color = "#9F583F", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M12 30 C11 24 11 18 14 14" stroke={color} strokeWidth="3.2" strokeLinecap="round" />
    <ellipse cx="19" cy="11" rx="5" ry="4.4" fill={color} />
    <path d="M23 11 q4 0.5 4 4.5" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
    <line x1="16" y1="7.5" x2="15" y2="3.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="14.8" cy="3" r="1.7" fill={color} />
    <line x1="22" y1="7.5" x2="23" y2="3.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="23.2" cy="3" r="1.7" fill={color} />
    <circle cx="17.5" cy="10.5" r="1.1" fill="#FBF6F3" />
  </svg>
);

export default function Recommendations() {
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [openCat, setOpenCat] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/rec/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/rec/subcategories").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/rec/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const subsOf = (catId) => subs.filter((s) => s.category_id === catId);
  const productsOfSub = (subId) => products.filter((p) => p.subcategory_id === subId).length;
  const catProductCount = (catId) => products.filter((p) => p.category_id === catId).length;

  return (
    <div className="relative z-10">
      <Header />

      <div className="relative w-full overflow-hidden" style={{ minHeight: "100vh", backgroundColor: "#E3B667" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/savanna-bg.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
          }}
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(240,200,120,0.30) 0%, rgba(226,170,90,0.5) 55%, rgba(205,145,66,0.62) 100%)" }} />
        <img src="/giraffe-marker.png" alt="" aria-hidden="true" className="absolute pointer-events-none select-none hidden lg:block" style={{ right: 8, bottom: 30, height: 360, opacity: 0.96, transform: "scaleX(-1)", filter: "drop-shadow(0 14px 16px rgba(0,0,0,0.32))" }} />

        {/* wooden title board */}
        <div className="relative z-10 flex justify-center pt-14 pb-8 px-6">
          <div
            className="relative text-center rounded-xl px-8 md:px-14 py-6 md:py-8 max-w-2xl w-full"
            style={{
              background: "linear-gradient(180deg,#9A6533 0%,#7A4E27 60%,#6B4322 100%)",
              boxShadow: "0 14px 30px rgba(60,36,18,0.5), inset 0 2px 0 rgba(255,255,255,0.15), inset 0 -6px 14px rgba(0,0,0,0.3)",
              border: "3px solid #5A3A1E",
            }}
          >
            <span className="absolute top-2 left-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <span className="absolute top-2 right-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <span className="absolute bottom-2 left-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <span className="absolute bottom-2 right-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <h1 className="font-display font-bold tracking-wide text-3xl md:text-5xl text-[#F5E6C8]" style={{ textShadow: "0 2px 0 rgba(0,0,0,0.35)" }}>
              LE GIRAFFE CONSIGLIANO
            </h1>
            <p className="font-display text-lg md:text-2xl text-[#EAD3A6] mt-2 tracking-wide">Il Cassetto delle Giraffe</p>
            <p className="label-ui text-[#D8BC8A] mt-1">Prodotti scelti e recensiti con cura</p>
          </div>
        </div>

        {/* vertical ordered list */}
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-24 space-y-4">
          {cats.length === 0 && (
            <div className="rounded-2xl bg-white/85 py-16 text-center">
              <p className="font-display text-2xl italic text-[#7A4A22]">Presto qui troverai i primi consigli.</p>
            </div>
          )}
          {cats.map((c, i) => {
            const color = COLORS[i % COLORS.length];
            const catSubs = subsOf(c.id);
            const isOpen = openCat === c.id;
            const count = catProductCount(c.id);
            return (
              <div key={c.id} data-testid={`rec-category-${c.name}`}>
                <motion.button
                  onClick={() => setOpenCat(isOpen ? null : c.id)}
                  whileHover={{ x: 4 }}
                  className="w-full text-left rounded-2xl overflow-hidden flex items-stretch shadow-lg"
                  style={{ background: `linear-gradient(90deg, ${color}2E 0%, rgba(255,250,240,0.96) 42%)`, border: `1.5px solid ${color}66` }}
                  data-testid={`rec-category-btn-${c.name}`}
                >
                  <div className="flex items-center justify-center shrink-0" style={{ width: 66, background: color }}>
                    <span className="font-display font-bold text-2xl text-white" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex items-center pl-4 pr-1">
                    <span className="flex items-center justify-center rounded-full" style={{ width: 46, height: 46, background: `${color}22` }}>
                      <GiraffeIcon color={color} size={26} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 py-4 pr-3 pl-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold uppercase tracking-wide text-[#3A2415]" style={{ fontSize: 18 }}>
                        {c.name}
                      </span>
                      {count > 0 && (
                        <span className="label-ui text-white rounded-full px-2 leading-5" style={{ fontSize: 10, background: color }}>
                          {count}
                        </span>
                      )}
                    </div>
                    <p className="font-body text-[#5A4326] leading-snug mt-1" style={{ fontSize: 13.5 }}>
                      {catSubs.length} sottocategorie
                    </p>
                  </div>
                  <div className="flex items-center pr-5" style={{ color }}>
                    <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                      <ChevronDown size={22} />
                    </motion.span>
                  </div>
                </motion.button>

                {/* expanded subcategories */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="subs"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-2xl mt-2 p-4 md:p-5 space-y-3" style={{ background: "rgba(255,255,255,0.7)", border: `1px dashed ${color}80` }} data-testid={`rec-panel-${c.name}`}>
                        {catSubs.length === 0 ? (
                          <p className="font-body italic text-[#7A4A22] text-center py-6">
                            Ancora nessuna sottocategoria. Torna presto!
                          </p>
                        ) : (
                          catSubs.map((s) => {
                            const count = productsOfSub(s.id);
                            return (
                              <Link
                                key={s.id}
                                to={`/le-giraffe-consigliano/sottocategoria/${s.id}`}
                                data-testid={`rec-subcategory-${s.name}`}
                                className="block"
                              >
                                <motion.div
                                  whileHover={{ x: 3 }}
                                  className="w-full text-left rounded-xl overflow-hidden flex items-center gap-3 py-3 px-4"
                                  style={{ background: `${color}1A`, border: `1px solid ${color}55` }}
                                  data-testid={`rec-subcategory-btn-${s.name}`}
                                >
                                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                                  <span className="font-display font-bold tracking-wide text-[#3A2415] flex-1" style={{ fontSize: 16 }}>
                                    {s.name}
                                  </span>
                                  {count > 0 && (
                                    <span className="label-ui text-white rounded-full px-2 leading-5" style={{ fontSize: 10, background: color }}>
                                      {count}
                                    </span>
                                  )}
                                  <ArrowRight size={18} style={{ color }} />
                                </motion.div>
                              </Link>
                            );
                          })
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {cats.length > 0 && (
            <div className="pt-6 text-center">
              <p className="font-display text-lg text-[#7A3B18] italic">
                Alcuni link sono affiliati: acquistando tramite essi sostieni il blog.
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
